import DialogContentText from "@mui/material/DialogContentText";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import { styled } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";

import GenericModal from "../../../components/modal-closebtn";
import { useCategories } from "../../../hooks/categories";
import { useProcessDefinitions } from "../../../hooks/process_definitions";
import { language } from "../../../language";
import Loading from "../../loading";

const LabelText = styled(Typography)(({ theme }) => ({
  fontWeight: "inherit",
  flexGrow: 1,
}));

function BpmSelectProcess({ handleCancel, onSelect, openDialog }) {
  const [selectedProcess, setSelectedProcess] = useState(false);
  const [selectedCategoryKey, setSelectedCategoryKey] = useState(false);

  const { data: categoryList } = useCategories({});
  const {
    data: processDefinitionList,
    refetch: fetchGetProcessDefinitionList,
    isLoading: processDefinitionListLoad,
  } = useProcessDefinitions({
    enabled: false,
    params: {
      isPageable: false,
      sort: "name",
      sortOrder: "asc",
      latestVersion: true,
      ...(selectedCategoryKey && selectedCategoryKey !== "__@ALL@"
        ? { byCategory: selectedCategoryKey }
        : {}),
    },
  });

  // carrega process-definitions de acordo com a categoria selecionada
  useEffect(() => {
    if (selectedCategoryKey === false) return;
    fetchGetProcessDefinitionList();
  }, [selectedCategoryKey, fetchGetProcessDefinitionList]);

  const clearState = () => {
    setSelectedProcess(false);
    setSelectedCategoryKey(false);
  };

  const onCancel = () => {
    handleCancel();
    clearState();
  };

  const handleSave = () => {
    onSelect(selectedProcess);
    clearState();
  };

  const handleOnChange = (event) => {
    setSelectedProcess(event.target.value);
  };

  const handleOnChangeCategory = (event) => {
    setSelectedCategoryKey(event.target.value);
  };

  const getCategoryOptions = (categories) => {
    const options = [];

    options.push(
      <MenuItem key="__@ALL@" value="__@ALL@">
        {language.allCategory}
      </MenuItem>,
    );

    if (categories.length) {
      categories.forEach((c) => {
        if (c.name) {
          options.push(
            <MenuItem key={c.name} value={c.name}>
              {c.name}
            </MenuItem>,
          );
        }
      });
    }

    return options;
  };

  const getProcessDefinitionOptions = (processDefinitions) => {
    const options = [];

    if (processDefinitions?.content) {
      processDefinitions?.content.forEach((p) => {
        options.push(
          <MenuItem key={p.id} value={p}>
            <LabelText variant="body2">{p.name}</LabelText>
            <Typography variant="caption" color="secondary">
              {`${language.versionNumber}: ${p.version}`}
            </Typography>
          </MenuItem>,
        );
      });
    }

    return options;
  };

  const isInvalid = selectedProcess === false || selectedCategoryKey === false;

  const _categoryOptions = categoryList && getCategoryOptions(categoryList);
  const _processOptions =
    processDefinitionList && getProcessDefinitionOptions(processDefinitionList);

  const isLoadingProcessDefinitions = processDefinitionListLoad ? (
    <Loading />
  ) : (
    ""
  );

  return (
    <GenericModal
      open={openDialog}
      handleClose={onCancel}
      title={language.listProcesstitle}
      btnActionTitle={language.selectProcess}
      handleSubmit={handleSave}
      disabledAction={isInvalid}
    >
      <DialogContentText>{language.selectProcessContent}</DialogContentText>
      <Stack spacing={2} sx={{ marginY: 2 }}>
        <TextField
          select
          label={language.category}
          onChange={handleOnChangeCategory}
          error={undefined}
          placeholder={language.process}
          fullWidth
          value={selectedCategoryKey === false ? "" : selectedCategoryKey}
        >
          {_categoryOptions}
        </TextField>
        <TextField
          select
          label={language.categoryProcess}
          onChange={handleOnChange}
          error={undefined}
          placeholder={language.process}
          fullWidth
          disabled={!selectedCategoryKey}
          value={selectedProcess || ""}
          renderValue={(selected) => (selected ? selected.name : "")}
        >
          {_processOptions}
        </TextField>
      </Stack>
    </GenericModal>
  );
}

BpmSelectProcess.propTypes = {
  handleCancel: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  openDialog: PropTypes.bool.isRequired,
};

export default BpmSelectProcess;
